import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Building2 } from 'lucide-react';
import { useApp } from '../context/AppContext';

export default function DepartmentSelector() {
  const { activeDepartment, setActiveDepartment, departmentsEnabled, accessibleDepartments } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const listboxRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  if (!departmentsEnabled || accessibleDepartments.length < 2) return null;

  const sortedDepts = [...accessibleDepartments].sort((a, b) => a.name.localeCompare(b.name));
  const displayName = activeDepartment?.name ?? 'All Departments';

  function handleSelect(deptOrNull) {
    setActiveDepartment(deptOrNull);
    setOpen(false);
  }

  function handleButtonKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => {
        const first = listboxRef.current?.querySelector('[role="option"]');
        first?.focus();
      });
    }
  }

  function handleListboxKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      ref.current?.querySelector('.dept-selector-btn')?.focus();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const options = [...(listboxRef.current?.querySelectorAll('[role="option"]') ?? [])];
      const idx = options.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' && idx < options.length - 1) options[idx + 1]?.focus();
      if (e.key === 'ArrowUp' && idx > 0) options[idx - 1]?.focus();
    }
  }

  return (
    <div className="dept-selector" ref={ref}>
      <button
        className={`dept-selector-btn${open ? ' open' : ''}`}
        onClick={() => setOpen(o => !o)}
        onKeyDown={handleButtonKeyDown}
        aria-label="Select department"
        aria-expanded={open}
      >
        <Building2 size={13} />
        <span className="dept-selector-label">{displayName}</span>
        <ChevronDown size={12} className={`dept-selector-chevron${open ? ' rotated' : ''}`} />
      </button>

      {open && (
        <div
          className="dept-selector-dropdown"
          role="listbox"
          ref={listboxRef}
          onKeyDown={handleListboxKeyDown}
        >
          <button
            role="option"
            aria-selected={activeDepartment === null}
            className={`dept-selector-option${activeDepartment === null ? ' active' : ''}`}
            onClick={() => handleSelect(null)}
          >
            All Departments
          </button>
          {sortedDepts.map(dept => (
            <button
              key={dept.id}
              role="option"
              aria-selected={activeDepartment?.id === dept.id}
              className={`dept-selector-option${activeDepartment?.id === dept.id ? ' active' : ''}`}
              onClick={() => handleSelect(dept)}
            >
              {dept.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
